import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Star, Plus, Trash2, MessageSquare, RefreshCw, Sparkles, Send, Link2, MapPin } from 'lucide-react'
import { api } from '@/lib/api'
import {
  AiReviewSuggestionInline,
  type AiReviewSuggestionLite,
} from '@/components/marketing/AiReviewSuggestionInline'

interface Source {
  id: string
  provider: 'GBP' | 'FACEBOOK' | 'TRUSTPILOT' | 'GENERIC_URL'
  displayName: string
  publicUrl: string | null
  monitorOnly: boolean
  autoReplyEnabled: boolean
  autoReplyMinRating: number
  active: boolean
  lastSyncAt: string | null
  /** Conectada a Google (tiene refresh token). */
  connected: boolean
  /** Recurso de la ubicación Google vinculada: "accounts/A/locations/L". */
  externalAccountId: string | null
  _count: { reviews: number; requests: number }
}

interface Review {
  id: string
  reviewerName: string | null
  rating: number | null
  isRecommended: boolean | null
  body: string | null
  sentiment: 'POS' | 'NEU' | 'NEG' | 'MIXED' | null
  replyText: string | null
  replyAt: string | null
  publishedAt: string
  aiSuggestionId: string | null
  source: { id: string; displayName: string; provider: string }
}

interface ReviewRequest {
  id: string
  clientStubId: string
  channel: string
  status: string
  sentAt: string | null
  clickedAt: string | null
  convertedAt: string | null
  redirectToken: string
  createdAt: string
  source: { displayName: string; provider: string }
}

interface PendingAiSuggestion {
  id: string
  draftText: string
  confidenceScore: string | number | null
  status: 'PENDING'
  createdAt: string
  review: {
    id: string
    rating: number | null
    body: string | null
    reviewerName: string | null
    source: { displayName: string; provider: string }
  }
  botProfile: { id: string; name: string; mode: string; model: string }
}

type Tab = 'sources' | 'reviews' | 'requests' | 'ai-replies'

const GOOGLE_MSG: Record<string, string> = {
  connected: 'Cuenta de Google conectada y ubicación vinculada. Ya puedes sincronizar reseñas reales.',
  connected_pick_location:
    'Cuenta de Google conectada. Tienes varias ubicaciones: elige cuál vincular con «Elegir ubicación».',
  'error:bad_state': 'La conexión con Google caducó o no era válida. Vuelve a intentarlo.',
  'error:no_refresh_token':
    'Google no devolvió refresh token. Revoca el acceso en tu cuenta Google y reconecta.',
  'error:exchange': 'Google rechazó el intercambio de credenciales. Revisa client ID/secret y el redirect URI.',
  'error:missing_params': 'Faltaron parámetros en la respuesta de Google.',
  'error:source_gone': 'La source ya no existe.',
}

export function ReputationPage() {
  const [tab, setTab] = useState<Tab>('sources')
  const [sources, setSources] = useState<Source[]>([])
  const [reviews, setReviews] = useState<Review[]>([])
  const [requests, setRequests] = useState<ReviewRequest[]>([])
  const [pendingAi, setPendingAi] = useState<PendingAiSuggestion[]>([])
  const [reviewsProvider, setReviewsProvider] = useState<'mock' | 'google-business'>('mock')
  const [googleMsg, setGoogleMsg] = useState<{ text: string; error: boolean } | null>(null)

  async function load() {
    const [s, r, q, ai, cfg] = await Promise.all([
      api<{ items: Source[] }>('/v1/marketing/reputation/sources'),
      api<{ items: Review[] }>('/v1/marketing/reputation/reviews'),
      api<{ items: ReviewRequest[] }>('/v1/marketing/reputation/requests'),
      api<{ items: PendingAiSuggestion[] }>('/v1/marketing/reputation/ai-replies/pending'),
      api<{ reviewsProvider: 'mock' | 'google-business' }>('/v1/marketing/reputation/config').catch(
        () => ({ reviewsProvider: 'mock' as const }),
      ),
    ])
    setSources(s.items)
    setReviews(r.items)
    setRequests(q.items)
    setPendingAi(ai.items)
    setReviewsProvider(cfg.reviewsProvider)
  }

  useEffect(() => {
    void load()
  }, [])

  // Feedback del callback OAuth: ?google=<estado>. Lo mostramos y limpiamos la URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const g = params.get('google')
    if (!g) return
    setGoogleMsg({ text: GOOGLE_MSG[g] ?? `Google: ${g}`, error: g.startsWith('error') })
    params.delete('google')
    const qs = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
  }, [])

  // Indexa suggestions PENDING por reviewId para el inline en ReviewsTab.
  const pendingByReviewId = new Map<string, PendingAiSuggestion>()
  pendingAi.forEach((s) => pendingByReviewId.set(s.review.id, s))

  return (
    <div className="p-6 max-w-6xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <Star className="size-5 text-blueprint-700" />
          Reputation
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          Google Business Profile + Facebook Pages · request review por SMS/WA/Email ·
          smart routing con link público a TODOS + form privado opcional (no condicional al
          rating) · cumplimiento FTC + Google ToS
        </p>
        <div
          className={[
            'mt-2 inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-mono border',
            reviewsProvider === 'google-business'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-amber-50 border-amber-200 text-amber-900',
          ].join(' ')}
        >
          {reviewsProvider === 'google-business'
            ? 'Google Business Profile REAL · conecta cada source GBP para sync + respuestas por API'
            : 'Modo mock (PROVIDER_REVIEWS=mock) · activa google-business en el servidor para reseñas reales'}
        </div>
        {googleMsg && (
          <div
            className={[
              'mt-2 text-xs px-3 py-2 rounded border',
              googleMsg.error
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-emerald-50 border-emerald-200 text-emerald-800',
            ].join(' ')}
          >
            {googleMsg.text}
          </div>
        )}
      </header>

      <div className="flex gap-1 border-b border-ink-900/10 mb-4">
        {(
          [
            { k: 'sources', label: 'Sources', count: sources.length },
            { k: 'reviews', label: 'Reviews', count: reviews.length },
            { k: 'requests', label: 'Requests', count: requests.length },
            { k: 'ai-replies', label: 'Borradores IA', count: pendingAi.length, urgent: true },
          ] as { k: Tab; label: string; count: number; urgent?: boolean }[]
        ).map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={[
              'px-3 py-2 text-xs font-medium border-b-2 -mb-px inline-flex items-center gap-1.5',
              tab === t.k
                ? 'border-blueprint-600 text-blueprint-700'
                : 'border-transparent text-ink-700/60 hover:text-ink-900',
            ].join(' ')}
          >
            <span>{t.label}</span>
            {t.urgent && t.count > 0 ? (
              <span
                className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-blueprint-600 text-white"
                aria-label={`${t.count} borradores pendientes de revisión humana`}
              >
                {t.count}
              </span>
            ) : (
              <span className="text-ink-700/50">({t.count})</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'sources' && (
        <SourcesTab sources={sources} reload={load} reviewsProvider={reviewsProvider} />
      )}
      {tab === 'reviews' && (
        <ReviewsTab reviews={reviews} pendingByReviewId={pendingByReviewId} reload={load} />
      )}
      {tab === 'requests' && <RequestsTab requests={requests} reload={load} />}
      {tab === 'ai-replies' && <AiRepliesTab items={pendingAi} reload={load} />}
    </div>
  )
}

interface GoogleLocation {
  resource: string
  title: string
  account: string
}

function SourcesTab({
  sources,
  reload,
  reviewsProvider,
}: {
  sources: Source[]
  reload: () => void
  reviewsProvider: 'mock' | 'google-business'
}) {
  const [draftName, setDraftName] = useState('')
  const [draftProvider, setDraftProvider] = useState<Source['provider']>('GBP')
  const [draftUrl, setDraftUrl] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rowMsg, setRowMsg] = useState<Record<string, { text: string; error: boolean }>>({})
  // Selector de ubicación de Google (cuando la cuenta tiene varias).
  const [pickerFor, setPickerFor] = useState<string | null>(null)
  const [pickerItems, setPickerItems] = useState<GoogleLocation[] | null>(null)
  const [pickerBusy, setPickerBusy] = useState(false)

  const googleReal = reviewsProvider === 'google-business'

  function setMsg(id: string, text: string, error: boolean) {
    setRowMsg((m) => ({ ...m, [id]: { text, error } }))
  }

  async function create(e: React.FormEvent) {
    e.preventDefault()
    await api('/v1/marketing/reputation/sources', {
      method: 'POST',
      body: JSON.stringify({
        provider: draftProvider,
        displayName: draftName,
        publicUrl: draftUrl || undefined,
      }),
    })
    setDraftName('')
    setDraftUrl('')
    reload()
  }

  async function remove(s: Source) {
    if (!confirm(`¿Eliminar la source "${s.displayName}"?`)) return
    await api(`/v1/marketing/reputation/sources/${s.id}`, { method: 'DELETE' })
    reload()
  }

  // Inicia el consentimiento OAuth → navega a Google (full page).
  async function connect(s: Source) {
    setBusyId(s.id)
    try {
      const { authUrl } = await api<{ authUrl: string }>(
        `/v1/marketing/reputation/sources/${s.id}/google/connect`,
      )
      window.location.href = authUrl
    } catch (e) {
      setMsg(s.id, e instanceof Error ? e.message : String(e), true)
      setBusyId(null)
    }
  }

  async function disconnect(s: Source) {
    if (!confirm(`¿Desconectar "${s.displayName}" de Google? (borra los tokens)`)) return
    await api(`/v1/marketing/reputation/sources/${s.id}/google/disconnect`, { method: 'POST' })
    reload()
  }

  async function sync(s: Source) {
    setBusyId(s.id)
    setMsg(s.id, '', false)
    try {
      const res = await api<{ mode: string; created: number; updated?: number }>(
        `/v1/marketing/reputation/sources/${s.id}/sync`,
        { method: 'POST' },
      )
      const label = res.mode === 'google' ? 'Google' : 'mock'
      setMsg(
        s.id,
        `Sync ${label}: ${res.created} nuevas${res.updated != null ? `, ${res.updated} actualizadas` : ''}.`,
        false,
      )
      reload()
    } catch (e) {
      setMsg(s.id, e instanceof Error ? e.message : String(e), true)
    } finally {
      setBusyId(null)
    }
  }

  async function openPicker(s: Source) {
    setPickerFor(s.id)
    setPickerItems(null)
    setPickerBusy(true)
    try {
      const res = await api<{ items: GoogleLocation[] }>(
        `/v1/marketing/reputation/sources/${s.id}/google/locations`,
      )
      setPickerItems(res.items)
    } catch (e) {
      setMsg(s.id, e instanceof Error ? e.message : String(e), true)
      setPickerFor(null)
    } finally {
      setPickerBusy(false)
    }
  }

  async function chooseLocation(sourceId: string, resource: string) {
    setPickerBusy(true)
    try {
      await api(`/v1/marketing/reputation/sources/${sourceId}/google/location`, {
        method: 'POST',
        body: JSON.stringify({ resource }),
      })
      setPickerFor(null)
      setPickerItems(null)
      reload()
    } catch (e) {
      setMsg(sourceId, e instanceof Error ? e.message : String(e), true)
    } finally {
      setPickerBusy(false)
    }
  }

  return (
    <>
      <form onSubmit={(e) => void create(e)} className="bp-card p-3 mb-4 grid grid-cols-12 gap-2">
        <select
          value={draftProvider}
          onChange={(e) => setDraftProvider(e.target.value as Source['provider'])}
          className="col-span-2 border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
        >
          <option value="GBP">Google Business</option>
          <option value="FACEBOOK">Facebook</option>
          <option value="TRUSTPILOT">Trustpilot</option>
          <option value="GENERIC_URL">Genérica</option>
        </select>
        <input
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          placeholder="nombre visible"
          required
          className="col-span-4 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
        />
        <input
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          placeholder="public URL (review link)"
          className="col-span-5 border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
        />
        <button type="submit" className="bp-btn-primary col-span-1 justify-center">
          <Plus className="size-3" />
        </button>
      </form>

      <div className="bp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
            <tr>
              <th className="px-3 py-2 text-left">Provider</th>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Conexión</th>
              <th className="px-3 py-2 text-right">Reviews</th>
              <th className="px-3 py-2 text-right">Requests</th>
              <th className="px-3 py-2 text-left">Último sync</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => {
              const isGbp = s.provider === 'GBP'
              const needsLocation = isGbp && s.connected && !s.externalAccountId
              const msg = rowMsg[s.id]
              return (
                <tr key={s.id} className="border-t border-ink-900/10 align-top">
                  <td className="px-3 py-2">
                    <span className="bp-pill">{s.provider}</span>
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {s.displayName}
                    {msg && (
                      <div
                        className={[
                          'mt-1 text-[11px] font-mono rounded px-2 py-1 max-w-sm',
                          msg.error
                            ? 'text-red-700 bg-red-50 border border-red-200'
                            : 'text-emerald-800 bg-emerald-50 border border-emerald-200',
                        ].join(' ')}
                      >
                        {msg.text}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {!isGbp ? (
                      <span className="text-ink-700/40">—</span>
                    ) : !googleReal ? (
                      <span className="text-amber-700" title="Activa PROVIDER_REVIEWS=google-business">
                        mock
                      </span>
                    ) : s.connected ? (
                      needsLocation ? (
                        <span className="text-amber-700">falta ubicación</span>
                      ) : (
                        <span className="text-emerald-700">conectada ✓</span>
                      )
                    ) : (
                      <span className="text-ink-700/50">sin conectar</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{s._count.reviews}</td>
                  <td className="px-3 py-2 text-right font-mono">{s._count.requests}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-ink-700/60">
                    {s.lastSyncAt ? new Date(s.lastSyncAt).toLocaleString('es-ES') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {isGbp && googleReal && !s.connected && (
                      <button
                        type="button"
                        onClick={() => void connect(s)}
                        className="bp-btn"
                        disabled={busyId === s.id}
                        title="Conectar la cuenta de Google (OAuth)"
                      >
                        <Link2 className="size-3" />
                        {busyId === s.id ? '…' : 'Conectar Google'}
                      </button>
                    )}
                    {isGbp && googleReal && needsLocation && (
                      <button
                        type="button"
                        onClick={() => void openPicker(s)}
                        className="bp-btn ml-1"
                        title="Elegir la ubicación de Google a sincronizar"
                      >
                        <MapPin className="size-3" /> Elegir ubicación
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void sync(s)}
                      className="bp-btn ml-1"
                      disabled={busyId === s.id}
                      title={
                        isGbp && googleReal
                          ? 'Sincroniza reseñas reales desde Google'
                          : 'Sync mock — genera reviews canned'
                      }
                    >
                      <RefreshCw className="size-3" />
                      {busyId === s.id ? 'Sync…' : isGbp && googleReal ? 'Sincronizar' : 'Sync (mock)'}
                    </button>
                    {isGbp && googleReal && s.connected && (
                      <button
                        type="button"
                        onClick={() => void disconnect(s)}
                        className="bp-btn ml-1"
                        title="Desconectar de Google"
                      >
                        Desconectar
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void remove(s)}
                      className="bp-btn text-red-700 ml-1"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </td>
                </tr>
              )
            })}
            {sources.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-xs text-ink-700/50">
                  Sin sources configuradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pickerFor && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
          onClick={() => setPickerFor(null)}
        >
          <div className="bp-card p-4 max-w-lg w-full bg-white" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
              <MapPin className="size-4 text-blueprint-700" /> Elige la ubicación de Google
            </h3>
            {pickerBusy && !pickerItems && (
              <p className="text-xs text-ink-700/60">Cargando ubicaciones…</p>
            )}
            {pickerItems && pickerItems.length === 0 && (
              <p className="text-xs text-ink-700/60">
                No se encontraron ubicaciones en esta cuenta de Google.
              </p>
            )}
            <ul className="space-y-1 max-h-80 overflow-auto">
              {pickerItems?.map((loc) => (
                <li key={loc.resource}>
                  <button
                    type="button"
                    onClick={() => void chooseLocation(pickerFor, loc.resource)}
                    disabled={pickerBusy}
                    className="w-full text-left px-3 py-2 rounded border border-ink-900/10 hover:bg-surface-alt text-sm"
                  >
                    <div className="font-medium">{loc.title}</div>
                    <div className="text-[11px] font-mono text-ink-700/50">{loc.resource}</div>
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-end">
              <button type="button" className="bp-btn" onClick={() => setPickerFor(null)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function ReviewsTab({
  reviews,
  pendingByReviewId,
  reload,
}: {
  reviews: Review[]
  pendingByReviewId: Map<string, PendingAiSuggestion>
  reload: () => void
}) {
  const [replyId, setReplyId] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [genBusy, setGenBusy] = useState<string | null>(null)
  const [genErr, setGenErr] = useState<string | null>(null)

  async function send(id: string) {
    await api(`/v1/marketing/reputation/reviews/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify({ replyText }),
    })
    setReplyId(null)
    setReplyText('')
    reload()
  }

  async function suggest(id: string) {
    setGenBusy(id)
    setGenErr(null)
    try {
      await api(`/v1/marketing/reputation/reviews/${id}/suggest-reply`, { method: 'POST' })
      reload()
    } catch (e) {
      setGenErr(e instanceof Error ? e.message : String(e))
    } finally {
      setGenBusy(null)
    }
  }

  if (reviews.length === 0) {
    return (
      <div className="bp-card p-8 text-center text-sm text-ink-700/50">
        Sin reviews todavía. Lanza un sync mock desde la pestaña Sources.
      </div>
    )
  }

  return (
    <>
      {genErr && (
        <div className="mb-2 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {genErr}
        </div>
      )}
      <ul className="space-y-2">
        {reviews.map((r) => {
          const pendingAi = pendingByReviewId.get(r.id)
          return (
            <li key={r.id} className="bp-card p-3">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="bp-pill">{r.source.provider}</span>
                    {r.rating != null && (
                      <span className="font-mono text-amber-600">{'★'.repeat(r.rating)}</span>
                    )}
                    {r.sentiment && (
                      <span
                        className={[
                          'bp-pill',
                          r.sentiment === 'POS'
                            ? 'bg-emerald-50 text-emerald-700'
                            : r.sentiment === 'NEG'
                              ? 'bg-red-50 text-red-700'
                              : 'bg-ink-900/10 text-ink-700/60',
                        ].join(' ')}
                      >
                        {r.sentiment}
                      </span>
                    )}
                    <span className="font-medium">{r.reviewerName ?? '(anónimo)'}</span>
                    <span className="ml-auto text-[10px] font-mono text-ink-700/50">
                      {new Date(r.publishedAt).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                  {r.body && <p className="mt-2 text-sm whitespace-pre-wrap">{r.body}</p>}
                  {r.replyText && (
                    <div className="mt-2 ml-4 pl-3 border-l-2 border-blueprint-300">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-blueprint-700 mb-0.5">
                        respuesta · {r.replyAt && new Date(r.replyAt).toLocaleDateString('es-ES')}
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{r.replyText}</p>
                    </div>
                  )}
                </div>
                {!r.replyText && replyId !== r.id && (
                  <div className="flex flex-col gap-1">
                    {!pendingAi && !r.aiSuggestionId && (
                      <button
                        type="button"
                        onClick={() => void suggest(r.id)}
                        className="bp-btn"
                        disabled={genBusy === r.id}
                      >
                        <Sparkles className="size-3" />
                        {genBusy === r.id ? 'Generando…' : 'Generar borrador IA'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setReplyId(r.id)
                        setReplyText('')
                      }}
                      className="bp-btn"
                    >
                      <MessageSquare className="size-3" /> Responder manual
                    </button>
                  </div>
                )}
              </div>

              {pendingAi && (
                <AiReviewSuggestionInline
                  reviewId={r.id}
                  suggestion={
                    {
                      id: pendingAi.id,
                      draftText: pendingAi.draftText,
                      confidenceScore: pendingAi.confidenceScore,
                      status: 'PENDING',
                      botProfile: pendingAi.botProfile,
                    } satisfies AiReviewSuggestionLite
                  }
                  onChange={reload}
                />
              )}

              {replyId === r.id && (
                <div className="mt-3 border-t border-ink-900/10 pt-3">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={3}
                    placeholder="respuesta…"
                    className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
                  />
                  <div className="mt-2 flex gap-2 justify-end">
                    <button type="button" onClick={() => setReplyId(null)} className="bp-btn">
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={() => void send(r.id)}
                      className="bp-btn-primary"
                      disabled={!replyText.trim()}
                    >
                      Publicar
                    </button>
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </>
  )
}

function RequestsTab({ requests, reload }: { requests: ReviewRequest[]; reload: () => void }) {
  const [sending, setSending] = useState<string | null>(null)
  // Mapeamos el error por request para que el operador asocie el blocked a la
  // fila concreta y no a un banner global ambiguo (LSSI: la información de
  // compliance debe ser inequívoca).
  const [errByRequestId, setErrByRequestId] = useState<Record<string, string>>({})

  function describeBlocked(code: string | undefined, detail: string | undefined): string {
    switch (code) {
      case 'NO_CONSENT':
        return 'No se envió: falta consent del cliente para este canal (LSSI).'
      case 'DND':
        return 'No se envió: el cliente está en DND para este canal.'
      case 'SUPPRESSED':
        return 'No se envió: la dirección está en lista de supresión.'
      case 'NO_ADDRESS':
        return `No se envió: ${detail ?? 'el cliente no tiene dirección para este canal.'}`
      case 'NO_PUBLIC_URL':
        return 'No se envió: la source no tiene URL pública configurada (FTC).'
      case 'NOT_PENDING':
        return 'No se envió: el request ya no está en estado PENDING.'
      case 'MAX_RETRIES':
        return 'No se envió: se alcanzó el máximo de reintentos.'
      case 'PROVIDER_ERROR':
        return `No se envió: fallo del proveedor (${detail ?? 'sin detalle'}).`
      default:
        return detail || 'No se envió: motivo desconocido.'
    }
  }

  async function send(r: ReviewRequest) {
    // LSSI + coste: comunicación comercial real al cliente. Confirmamos antes
    // de disparar para evitar envíos accidentales por click en fila errónea.
    const confirmed = window.confirm(
      `¿Enviar review request por ${r.channel} al cliente ${r.clientStubId.slice(0, 8)}…?`,
    )
    if (!confirmed) return
    setSending(r.id)
    // Limpiamos errores previos al iniciar un nuevo envío (cualquier request).
    setErrByRequestId({})
    try {
      await api(`/v1/marketing/reputation/requests/${r.id}/send`, { method: 'POST' })
      reload()
    } catch (e) {
      // api() lanza Error con texto "API 409 ...: <body>". Intentamos extraer
      // el JSON {blocked, code, detail} para mostrar mensaje legible.
      const raw = e instanceof Error ? e.message : String(e)
      const bodyMatch = raw.match(/:\s*(\{[\s\S]*\})\s*$/)
      let friendly = raw
      if (bodyMatch) {
        try {
          const parsed = JSON.parse(bodyMatch[1]!) as {
            blocked?: boolean
            code?: string
            detail?: string
          }
          if (parsed.blocked) friendly = describeBlocked(parsed.code, parsed.detail)
        } catch {
          /* sin cambios — mantenemos raw */
        }
      }
      setErrByRequestId({ [r.id]: friendly })
    } finally {
      setSending(null)
    }
  }

  if (requests.length === 0) {
    return (
      <div className="bp-card p-8 text-center text-sm text-ink-700/50">
        Sin requests todavía. Se generan desde el workflow post-Opportunity.
      </div>
    )
  }
  return (
    <>
      <div className="bp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
            <tr>
              <th className="px-3 py-2 text-left">Cliente</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-left">Canal</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Link</th>
              <th className="px-3 py-2 text-right">Creado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className="border-t border-ink-900/10">
                <td className="px-3 py-2">
                  <Link
                    to={`/marketing/clients/${r.clientStubId}`}
                    className="text-blueprint-700"
                  >
                    {r.clientStubId.slice(0, 12)}…
                  </Link>
                </td>
                <td className="px-3 py-2">{r.source.displayName}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.channel}</td>
                <td className="px-3 py-2">
                  <span
                    className={[
                      'bp-pill',
                      r.status === 'CONVERTED'
                        ? 'bg-emerald-50 text-emerald-700'
                        : r.status === 'CLICKED'
                          ? 'bg-blueprint-50 text-blueprint-700'
                          : r.status === 'SENT'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-ink-900/10 text-ink-700/60',
                    ].join(' ')}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-blueprint-700">
                  /r/{r.redirectToken.slice(0, 16)}…
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs text-ink-700/60">
                  {new Date(r.createdAt).toLocaleString('es-ES')}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status === 'PENDING' && (
                    <button
                      type="button"
                      onClick={() => void send(r)}
                      className="bp-btn-primary"
                      disabled={sending === r.id}
                    >
                      <Send className="size-3" />
                      {sending === r.id ? 'Enviando…' : 'Enviar ahora'}
                    </button>
                  )}
                  {errByRequestId[r.id] && (
                    <div className="mt-1 text-[11px] font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 max-w-xs ml-auto text-left">
                      {errByRequestId[r.id]}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function AiRepliesTab({
  items,
  reload,
}: {
  items: PendingAiSuggestion[]
  reload: () => void
}) {
  if (items.length === 0) {
    return (
      <div className="bp-card p-8 text-center text-sm text-ink-700/50">
        Sin borradores IA pendientes de revisión. Lanza un sync mock o genera un borrador
        desde la pestaña Reviews.
      </div>
    )
  }
  return (
    <ul className="space-y-2">
      {items.map((s) => (
        <li key={s.id} className="bp-card p-3">
          <div className="flex items-center gap-2">
            <span className="bp-pill">{s.review.source.provider}</span>
            {s.review.rating != null && (
              <span className="font-mono text-amber-600">{'★'.repeat(s.review.rating)}</span>
            )}
            <span className="font-medium">{s.review.reviewerName ?? '(anónimo)'}</span>
            <span className="ml-auto text-[10px] font-mono text-ink-700/50">
              {new Date(s.createdAt).toLocaleString('es-ES')}
            </span>
          </div>
          {s.review.body && (
            <p className="mt-2 text-sm whitespace-pre-wrap text-ink-700/80">{s.review.body}</p>
          )}
          <AiReviewSuggestionInline
            reviewId={s.review.id}
            suggestion={{
              id: s.id,
              draftText: s.draftText,
              confidenceScore: s.confidenceScore,
              status: 'PENDING',
              botProfile: s.botProfile,
            }}
            onChange={reload}
          />
        </li>
      ))}
    </ul>
  )
}
